var _dec, _class, _InPlaceEdit;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2018 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { Flex } from '@instructure/ui-flex';
import { IconButton } from '@instructure/ui-buttons';
import { IconEditLine } from '@instructure/ui-icons';
import { logWarn as warn } from '@instructure/console';
import { createChainedFunction } from '@instructure/ui-utils';
import { withStyle } from '@instructure/emotion';
import { View } from '@instructure/ui-view';
import { Editable } from "../Editable/index.js";
import generateStyle from "./styles.js";
import { allowedProps } from "./props.js";
import { jsxs as _jsxs, jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
---
category: components
---
**/
let InPlaceEdit = (_dec = withStyle(generateStyle, null), _dec(_class = (_InPlaceEdit = class InPlaceEdit extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this._editButtonRef = null;
    this.handleRef = el => {
      this.ref = el;
    };
    this.handleEditButtonRef = el => {
      this._editButtonRef = el;
    };
    this.renderAll = ({
      getContainerProps,
      getViewerProps,
      getEditorProps,
      getEditButtonProps
    }) => {
      const flexDir = this.props.editButtonPlacement === 'start' ? 'row-reverse' : 'row';
      const justifyItems = flexDir === 'row-reverse' ? 'end' : 'start';
      const buttonMargin = this.props.editButtonPlacement === 'start' ? '0 xx-small 0 0' : '0 0 0 xx-small';
      return _jsxs(Flex, {
        display: this.props.inline ? 'inline-flex' : 'flex',
        direction: flexDir,
        justifyItems: justifyItems,
        ...getContainerProps(),
        children: [_jsxs(Flex.Item, {
          shouldGrow: true,
          shouldShrink: true,
          children: [this.renderEditor(getEditorProps()), this.renderViewer(getViewerProps())]
        }), _jsx(Flex.Item, {
          margin: buttonMargin,
          children: this.renderEditButton(getEditButtonProps())
        })]
      });
    };
    warn(props.readOnly ? props.mode === 'view' : true, '[InPlaceEdit] When readOnly is true, mode is forced to "view"');
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
  }
  renderEditor({
    mode,
    onBlur,
    editorRef,
    readOnly
  }) {
    var _this$props$styles;
    const _this$props3 = this.props,
      showFocusRing = _this$props3.showFocusRing,
      renderEditor = _this$props3.renderEditor;
    const isEditMode = !readOnly && mode === 'edit';
    return isEditMode ? _jsx(View, {
      as: "span",
      display: "block",
      withFocusOutline: showFocusRing,
      position: "relative",
      css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.inPlaceEdit,
      borderRadius: "medium",
      margin: "auto",
      children: renderEditor({
        onBlur,
        editorRef
      })
    }) : null;
  }
  renderViewer({
    readOnly,
    mode
  }) {
    return readOnly || mode === 'view' ? this.props.renderViewer() : null;
  }
  renderEditButton({
    buttonRef,
    ...rest
  }) {
    return this.props.renderEditButton({
      elementRef: createChainedFunction(this.handleEditButtonRef, buttonRef),
      ...rest
    });
  }

  // Render a default edit button, an icon button with the edit icon
  // the margin makes room for the focus ring

  render() {
    const _this$props4 = this.props,
      mode = _this$props4.mode,
      value = _this$props4.value,
      onChange = _this$props4.onChange,
      onChangeMode = _this$props4.onChangeMode,
      readOnly = _this$props4.readOnly;
    return _jsx(Editable, {
      mode: mode,
      onChangeMode: onChangeMode,
      render: this.renderAll,
      value: value,
      onChange: onChange,
      readOnly: readOnly,
      elementRef: this.handleRef
    });
  }
}, _InPlaceEdit.displayName = "InPlaceEdit", _InPlaceEdit.componentId = 'InPlaceEdit', _InPlaceEdit.allowedProps = allowedProps, _InPlaceEdit.defaultProps = {
  readOnly: false,
  showFocusRing: true,
  inline: true,
  editButtonPlacement: 'end'
}, _InPlaceEdit.renderDefaultEditButton = ({
  isVisible,
  readOnly,
  label,
  ...buttonProps
}) => {
  if (readOnly) {
    return null;
  }
  return _jsx(IconButton, {
    size: "small",
    screenReaderLabel: label,
    withBackground: false,
    withBorder: false,
    ...buttonProps,
    children: isVisible ? IconEditLine : null
  });
}, _InPlaceEdit)) || _class);
export default InPlaceEdit;
export { InPlaceEdit };